import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const VIEWPORT_MARGIN = 12;

export interface PopoverPosition {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
}

interface Options {
  /** Largura desejada do painel (encolhe em telas estreitas). */
  width: number;
  /** Altura usada para posicionar antes de o painel existir e poder ser medido. */
  estimatedHeight: number;
  /** Chamado ao abrir (para zerar o estado interno do painel). */
  onOpen?: () => void;
  /** Chamado ao fechar, por qualquer motivo. */
  onClose?: () => void;
}

/**
 * Painel flutuante preso a um botão. Quem usa renderiza o painel num portal (<body>), porque o topo do dashboard
 * corta o que passa da borda dele, e usa `position` como estilo (position: fixed).
 * Abre abaixo do botão; se não couber e houver espaço, abre acima; se nenhum couber, o painel rola por dentro.
 * Fecha com Esc (devolvendo o foco ao botão) e com clique fora, e acompanha o botão ao rolar ou redimensionar.
 */
export function usePopover({ width: desiredWidth, estimatedHeight, onOpen, onClose }: Options) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<PopoverPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Os callbacks mudam a cada render; os efeitos abaixo leem sempre a versão mais recente.
  const callbacks = useRef({ onOpen, onClose });
  useLayoutEffect(() => {
    callbacks.current = { onOpen, onClose };
  });

  const place = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const width = Math.min(desiredWidth, window.innerWidth - VIEWPORT_MARGIN * 2);
    const left = Math.max(VIEWPORT_MARGIN, Math.min(rect.right - width, window.innerWidth - width - VIEWPORT_MARGIN));

    const height = popoverRef.current?.offsetHeight ?? estimatedHeight;
    let top = rect.bottom + 8;
    if (top + height > window.innerHeight - VIEWPORT_MARGIN && rect.top - 8 - height >= VIEWPORT_MARGIN) top = rect.top - 8 - height;
    setPosition({ top, left, width, maxHeight: Math.max(160, window.innerHeight - top - VIEWPORT_MARGIN) });
  }, [desiredWidth, estimatedHeight]);

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    callbacks.current.onClose?.();
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  const toggle = useCallback(() => {
    if (open) return close(false);
    place();
    callbacks.current.onOpen?.();
    setOpen(true);
  }, [open, close, place]);

  useLayoutEffect(() => {
    if (!open) return;
    place(); // já com o painel montado, para medir a altura de verdade
    const onScroll = (e: Event) => {
      if (!popoverRef.current?.contains(e.target as Node)) place(); // rolar o painel por dentro não o move
    };
    window.addEventListener('resize', place);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    popoverRef.current?.focus();
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      close(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(true);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  return { open, position, triggerRef, popoverRef, toggle, close };
}
